import {merge} from 'lodash-es';
import EnvironmentUtils from '@spothero/utils/environment';

/** @module hooks/config */

const optionalEnv = (value: string | undefined) =>
    value && value !== 'undefined' ? value : undefined;

export interface AppConfig {
    auth0Domain: string | undefined;
    auth0ClientId: string | undefined;
    auth0RedirectUri: string | undefined;
    auth0V1APIAudience: string | undefined;
    auth0CPServiceAudience: string | undefined;
    basename: string;
    authAPIPath: string | undefined;
    authAPIUrl: string | undefined;
    spotHeroAPIUrl: string | undefined;
    spotHeroAPIPath: string | undefined;
    spotHeroAPIPathV2: string | undefined;
    cpAPIUrl: string | undefined;
    cpAPIUrlV2: string | undefined;
    cpAPIPath: string | undefined;
    envType: string | undefined;
    buildTag: string | undefined;
    biServiceAPIUrl: string | undefined;
    sentryAPIPublicKey: string | undefined;
    siteUrl: string | undefined;
    spotHeroMixpanelToken: string | undefined;
    segmentApiKey: string | undefined;
    isSegmentEnabled: boolean;
    wootricAccountToken: string | undefined;
    isWootricEnabled: boolean;
    forceWootricSurvey: boolean;
    debugSegmentInConsole: boolean;
    debugWootricInConsole: boolean;
    isTest: boolean;
    isDeployed: boolean;
    isProduction: boolean;
    isMobileDevice: boolean;
    wasReservationsReferredFromDashboard: boolean;
    cloudfrontViewerCountryCode: string | null;
    googleMapsApiKey: string | undefined;
    intercomAppId: string | undefined;
    set(opts: Partial<Omit<AppConfig, 'set'>>): void;
}

/**
 * Runtime configuration values driven by environment variables and build-time flags.
 * @constant
 */
const Config: AppConfig = {
    auth0Domain: process.env.VITE_AUTH0_DOMAIN,
    auth0ClientId: process.env.VITE_AUTH0_CLIENT_ID,
    auth0RedirectUri: optionalEnv(process.env.VITE_AUTH0_REDIRECT_URI),
    auth0V1APIAudience: process.env.VITE_AUTH0_V1_API_AUDIENCE,
    auth0CPServiceAudience: process.env.VITE_AUTH0_CP_SERVICE_AUDIENCE,
    basename: process.env.VITE_BASE_PATH || '/control-panel',
    authAPIPath: process.env.VITE_AUTH_API_PATH,
    authAPIUrl: process.env.VITE_AUTH_API_URL,
    spotHeroAPIUrl: process.env.VITE_SPOTHERO_API_URL,
    spotHeroAPIPath: process.env.VITE_SPOTHERO_API_PATH,
    spotHeroAPIPathV2: process.env.VITE_SPOTHERO_API_PATH_V2,
    cpAPIUrl: process.env.VITE_CP_API_URL,
    cpAPIUrlV2: process.env.VITE_CP_API_URL_V2,
    cpAPIPath: process.env.VITE_CP_API_PATH,
    envType: process.env.VITE_ENV_TYPE,
    buildTag: process.env.VITE_SH_BUILD_TAG,
    biServiceAPIUrl: process.env.VITE_BI_SERVICE_API_URL,
    sentryAPIPublicKey: process.env.VITE_SENTRY_API_PUBLIC_KEY,
    siteUrl: process.env.VITE_SITE_URL,
    spotHeroMixpanelToken: process.env.MIXPANEL_TOKEN,
    segmentApiKey: process.env.VITE_SEGMENT_KEY,
    isSegmentEnabled: true,
    wootricAccountToken: process.env.VITE_WOOTRIC_TOKEN,
    isWootricEnabled: false,
    forceWootricSurvey: false,
    debugSegmentInConsole: false,
    debugWootricInConsole: false,
    isTest: process.env.VITE_NODE_ENV === 'test',
    isDeployed: process.env.VITE_IS_DEPLOYED
        ? JSON.parse(process.env.VITE_IS_DEPLOYED)
        : false,
    isProduction: process.env.VITE_IS_PRODUCTION
        ? JSON.parse(process.env.VITE_IS_PRODUCTION)
        : false,
    isMobileDevice: EnvironmentUtils.isMobileDevice(),
    wasReservationsReferredFromDashboard: false,
    cloudfrontViewerCountryCode: null,
    googleMapsApiKey: process.env.VITE_GOOGLE_MAP_API_KEY,
    intercomAppId: process.env.VITE_INTERCOM_APP_ID,

    set(opts) {
        merge(this, opts);
    },
};

export default Config;
